import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {BaseService} from './base.service';
import {AlertService} from './alert.service';
import {GlobalService} from './global.service';
import {AppUrl} from '../constants/app-url';

@Injectable({
    providedIn: 'root'
})
export class ReferralApiService extends BaseService {
    constructor(public http: HttpClient,
                public alertService: AlertService,
                public globalService: GlobalService) {
        super(http, alertService, globalService);
    }

    getReferrals(params: any = {}): Observable<any> {
        return this.getRequest(AppUrl.REFERRALS(), params);
    }

    getReferral(id): Observable<any> {
        return this.getRequest(AppUrl.REFERRALS(id));
    }

    updateStatus(id, data): Observable<any> {
        return this.patchRequest(AppUrl.REFERRAL_STATUS(id), data);
    }

    followUp(id, data): Observable<any> {
        return this.putRequest(AppUrl.REFERRAL_FOLLOW_UP(id), data);
    }

    getFollowUp(id): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_FOLLOW_UP(id));
    }

    getReferrers(params: any = {}): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_PARTNERS(), params);
    }

    getReferrer(id): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_PARTNERS(id));
    }

    updateReferrerStatus(id, data): Observable<any> {
        return this.patchRequest(AppUrl.REFERRAL_PARTNER_STATUS(id), data);
    }

    getRedemptions(params: any = {}): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_REDEMPTIONS(), params);
    }

    getRedemptionsSummary(params: any = {}): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_REDEMPTIONS_SUMMARY(), params);
    }

    approveRedemption(id): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_REDEMPTION_APPROVE(id), {});
    }

    rejectRedemption(id, data): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_REDEMPTION_REJECT(id), data);
    }

    processRedemption(id): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_REDEMPTION_PROCESS(id), {});
    }

    markRedemptionPaid(id, data): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_REDEMPTION_MARK_PAID(id), data);
    }

    markRedemptionFailed(id, data): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_REDEMPTION_MARK_FAILED(id), data);
    }

    getKycRequests(params: any = {}): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_KYC(), params);
    }

    approveKyc(id, notes?: string): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_KYC_APPROVE(id), notes ? {notes} : {});
    }

    rejectKyc(id, data): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_KYC_REJECT(id), data);
    }

    // Private file (PAN/ID/bank-proof scan) behind auth — bypasses the JSON-mapping base
    // helpers and asks for a blob directly. The global InterceptorService still attaches the
    // Authorization/device headers to this request like any other.
    getKycDocumentBlob(kycId, documentId): Observable<Blob> {
        return this.http.get(AppUrl.REFERRAL_KYC_DOCUMENT(kycId, documentId), {responseType: 'blob'});
    }

    getPayoutAccounts(params: any = {}): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_PAYOUT_ACCOUNTS(), params);
    }

    verifyPayoutAccount(id, notes?: string): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_PAYOUT_ACCOUNT_VERIFY(id), notes ? {notes} : {});
    }

    rejectPayoutAccount(id, data): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_PAYOUT_ACCOUNT_REJECT(id), data);
    }

    getCampaigns(params: any = {}): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_CAMPAIGNS(), params);
    }

    createCampaign(data): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_CAMPAIGNS(), data);
    }

    updateCampaign(id, data): Observable<any> {
        return this.putRequest(AppUrl.REFERRAL_CAMPAIGNS(id), data);
    }

    getFraudFlags(params: any = {}): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_FRAUD(), params);
    }

    getFraudFlag(id): Observable<any> {
        return this.getRequest(AppUrl.REFERRAL_FRAUD(id));
    }

    resolveFraudFlag(id, data): Observable<any> {
        return this.postRequest(AppUrl.REFERRAL_FRAUD_RESOLVE(id), data);
    }
}
